/**
 * The app's ONE `ApiClient`.
 *
 * One instance, because it owns the single in-flight refresh: two clients
 * over the same token store would each spend the single-use refresh token,
 * and the second presentation revokes the whole family (`api-client.ts`).
 */
import { ApiClient } from "@repo/client/api";
import { createContext, useContext } from "react";
import { createTokenStore } from "../native";

/** The same fallback the diagnostics use: the VPS's local API. */
const DEFAULT_API_URL = "http://100.84.151.119:8787";

/** Normalised with no trailing slash, so no path becomes `//auth/...`. */
export const API_URL: string = (
  import.meta.env.VITE_API_URL || DEFAULT_API_URL
).replace(/\/+$/, "");

const sessionEndedListeners = new Set<() => void>();

/**
 * Listen for the session ending — a sign-out, a refresh the server refused, a
 * discarded session. The credential is already gone when this runs; a
 * listener may only clear local state. Returns the unsubscribe.
 */
export function onApiSessionEnded(listener: () => void): () => void {
  sessionEndedListeners.add(listener);
  return () => {
    sessionEndedListeners.delete(listener);
  };
}

export const api = new ApiClient({
  baseUrl: () => API_URL,
  onSessionEnded: () => {
    for (const listener of sessionEndedListeners) {
      listener();
    }
  },
  store: createTokenStore(),
});

const ApiContext = createContext<ApiClient>(api);

/** The `ApiClient`; a story or test may provide another through `ApiProvider`. */
export const ApiProvider = ApiContext.Provider;

export function useApi(): ApiClient {
  return useContext(ApiContext);
}
